import React, { useEffect, useMemo, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { supabase } from '../../lib/supabase';
import { useAuthStore } from '../../stores/useAuthStore';
import { useFacturasStore } from '../../stores/useFacturasStore';
import { appendHistorialPagoCreditoAFactura } from '../../lib/credito/appendHistorialPagoCreditoAFactura';
import { patchPagoAdjuntos, uploadPagoComprobante } from '../../lib/cobro/uploadPagoComprobante';
import { opcionesMetodoPagoRegistro } from './creditosConstants';
import { usePaymentMethodsStore } from '../../stores';
import type { CuotaLike } from './creditosUiHelpers';
import { cuotaMasAntiguaAbierta, saldoCuota } from './creditosUiHelpers';
import { ReciboPagoGenerating, ReciboPagoSuccessPanel } from './ReciboPagoSuccessPanel';
import {
  generateAndUploadReceipt,
  loadFacturaContextForRecibo,
  type GenerateReceiptResult,
  type ReciboFacturaContext,
} from '../../utils/reciboPagoService';

const fmtCop = (n: number) =>
  new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(n);

export type RegistrarPagoCreditoModalProps = {
  open: boolean;
  onClose: () => void;
  empresaId: string;
  creditoId: string | null;
  /** Si ya vienen cuotas (p. ej. desde el drawer), se usan; si no, se cargan por `creditoId`. */
  cuotasIniciales?: CuotaLike[] | null;
  onSuccess?: () => void;
};

export function RegistrarPagoCreditoModal({
  open,
  onClose,
  empresaId,
  creditoId,
  cuotasIniciales,
  onSuccess,
}: RegistrarPagoCreditoModalProps) {
  const { usuario, currentEmpresa } = useAuthStore();
  const fetchPaymentMethods = usePaymentMethodsStore((s) => s.fetchPaymentMethods);
  const paymentMethods = usePaymentMethodsStore((s) => s.paymentMethods);
  const methodOptions = useMemo(
    () =>
      opcionesMetodoPagoRegistro(
        paymentMethods.filter((m) => m.empresa_id === empresaId && m.is_active)
      ),
    [paymentMethods, empresaId]
  );
  const [cuotas, setCuotas] = useState<CuotaLike[]>([]);
  const [loading, setLoading] = useState(false);
  const [cuotaId, setCuotaId] = useState<string>('');
  const [monto, setMonto] = useState<string>('');
  const [metodo, setMetodo] = useState<string>('EFECTIVO');
  const [notas, setNotas] = useState('');
  const [comprobanteFile, setComprobanteFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [phase, setPhase] = useState<'form' | 'generating' | 'success'>('form');
  const [receipt, setReceipt] = useState<GenerateReceiptResult | null>(null);
  const [facturaCtx, setFacturaCtx] = useState<ReciboFacturaContext | null>(null);
  const [creditoSaldo, setCreditoSaldo] = useState(0);
  const [pagoSummary, setPagoSummary] = useState<{
    monto: number;
    saldoAnterior: number;
    saldoDespues: number;
  } | null>(null);

  useEffect(() => {
    if (!open) return;
    setPhase('form');
    setReceipt(null);
    setFacturaCtx(null);
    setPagoSummary(null);
    setNotas('');
    setComprobanteFile(null);
    if (cuotasIniciales && cuotasIniciales.length > 0) {
      setCuotas(cuotasIniciales);
      return;
    }
    if (!creditoId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('cuotas')
          .select('id, numero_cuota, monto_cuota, monto_pagado, fecha_vencimiento, estado')
          .eq('credito_id', creditoId)
          .eq('empresa_id', empresaId)
          .order('numero_cuota', { ascending: true });
        if (error) throw error;
        if (!cancelled) setCuotas((data as CuotaLike[]) || []);
      } catch {
        if (!cancelled) setCuotas([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, creditoId, empresaId, cuotasIniciales]);

  useEffect(() => {
    if (!open || !creditoId) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('creditos')
        .select('saldo_pendiente')
        .eq('id', creditoId)
        .maybeSingle();
      if (!cancelled && data) {
        setCreditoSaldo(Math.round(Number((data as { saldo_pendiente?: number }).saldo_pendiente) || 0));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, creditoId]);

  useEffect(() => {
    if (open && empresaId) void fetchPaymentMethods(empresaId);
  }, [open, empresaId, fetchPaymentMethods]);

  useEffect(() => {
    if (!open || methodOptions.length === 0) return;
    setMetodo((prev) =>
      methodOptions.some((o) => o.value === prev) ? prev : methodOptions[0]!.value
    );
  }, [open, creditoId, methodOptions]);

  const defaultCuota = useMemo(() => cuotaMasAntiguaAbierta(cuotas), [cuotas]);

  useEffect(() => {
    if (!open) return;
    if (defaultCuota) setCuotaId(defaultCuota.id);
  }, [open, defaultCuota]);

  const selected = useMemo(() => cuotas.find((c) => c.id === cuotaId) ?? null, [cuotas, cuotaId]);
  const saldoSel = selected ? saldoCuota(selected) : 0;

  useEffect(() => {
    if (!open || !selected) return;
    setMonto(String(saldoSel));
  }, [open, selected?.id, saldoSel]);

  const handleDone = () => {
    onSuccess?.();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!creditoId || !cuotaId || !usuario?.id) return;
    const raw = String(monto).replace(/\./g, '').replace(/[^\d]/g, '');
    const m = Math.round(Number(raw) || 0);
    if (m <= 0) {
      alert('Indica un monto mayor a 0.');
      return;
    }
    setSubmitting(true);
    try {
      const { data: credRow, error: credErr } = await supabase
        .from('creditos')
        .select('factura_id, cliente_id, monto_total, saldo_pendiente')
        .eq('id', creditoId)
        .maybeSingle();
      if (credErr) throw credErr;

      const saldoAnterior = Math.round(
        Number((credRow as { saldo_pendiente?: number } | null)?.saldo_pendiente ?? creditoSaldo) || 0
      );
      const saldoDespues = Math.max(0, saldoAnterior - m);

      let comprobanteUrl: string | null = null;
      let comprobantePath: string | null = null;
      if (comprobanteFile && currentEmpresa?.id) {
        const up = await uploadPagoComprobante({
          empresaId: currentEmpresa.id,
          ownerId: creditoId,
          file: comprobanteFile,
        });
        if (up.error) {
          console.error(up.error);
          alert('No se pudo subir el comprobante. El pago se registrará sin archivo.');
        } else {
          comprobanteUrl = up.url;
          comprobantePath = up.path;
        }
      }

      const { data: pagoRow, error } = await supabase
        .from('pagos_credito')
        .insert({
          credito_id: creditoId,
          cuota_id: cuotaId,
          empresa_id: empresaId,
          monto: m,
          metodo_pago: metodo || null,
          notas: notas.trim() || null,
          registrado_por: usuario.id,
        })
        .select('id')
        .single();
      if (error) throw error;

      if (pagoRow?.id && (comprobanteUrl || comprobantePath)) {
        await patchPagoAdjuntos('pagos_credito', pagoRow.id, {
          comprobante_url: comprobanteUrl,
          comprobante_path: comprobantePath,
        });
      }

      const facturaId = credRow && (credRow as { factura_id: string | null }).factura_id;
      const clienteId = (credRow as { cliente_id?: string | null } | null)?.cliente_id;
      const montoTotal = Math.round(Number((credRow as { monto_total?: number } | null)?.monto_total) || 0);

      setPagoSummary({ monto: m, saldoAnterior, saldoDespues });
      setPhase('generating');

      let reciboUrl: string | undefined;
      try {
        const ctx = await loadFacturaContextForRecibo(empresaId, facturaId, {
          total: montoTotal,
          clienteId,
        });
        setFacturaCtx(ctx);
        const result = await generateAndUploadReceipt(
          ctx,
          {
            pagoId: pagoRow.id,
            monto: m,
            metodoPago: metodo,
            saldoAnterior,
            saldoDespues,
            notas: notas.trim() || undefined,
          },
          {
            empresaId,
            companyName: currentEmpresa?.nombre || 'Empresa',
            companyLogo: currentEmpresa?.logo || null,
            companyEmail: currentEmpresa?.email || '',
            companyPhone: currentEmpresa?.telefono || '',
            companyAddress: currentEmpresa?.direccion || '',
          },
          'pagos_credito'
        );
        setReceipt(result);
        reciboUrl = result.reciboUrl || undefined;
      } catch (receiptErr) {
        console.warn('Recibo de abono (crédito):', receiptErr);
        setReceipt(null);
      }

      if (facturaId) {
        const nombreUser =
          [usuario.nombre, usuario.apellido].filter(Boolean).join(' ').trim() ||
          usuario.email ||
          'Usuario';
        const { error: histErr } = await appendHistorialPagoCreditoAFactura(supabase, {
          empresaId,
          facturaId,
          monto: m,
          metodoPago: metodo || '—',
          notas: notas.trim() || null,
          comprobanteUrl: comprobanteUrl || undefined,
          reciboUrl,
          creditoId,
          registradoPorNombre: nombreUser,
          registradoPorId: usuario.id,
        });
        if (histErr) {
          console.error('Historial factura (crédito):', histErr);
        }
      }

      if (empresaId) {
        void useFacturasStore.getState().fetchFacturas(empresaId);
      }

      setComprobanteFile(null);
      setPhase('success');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'No se pudo registrar el pago.');
      setPhase('form');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open || !creditoId) return null;

  return (
    <div className="fixed inset-0 z-[120] flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4">
      <div className="w-full max-w-md rounded-t-2xl bg-white shadow-xl sm:rounded-2xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
          <h2 className="text-base font-semibold text-gray-900">
            {phase === 'success' ? 'Abono confirmado' : 'Registrar pago'}
          </h2>
          <button
            type="button"
            className="rounded-full p-1 text-gray-500 hover:bg-gray-100"
            onClick={phase === 'success' ? handleDone : onClose}
            aria-label="Cerrar"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        {phase === 'generating' && <ReciboPagoGenerating />}
        {phase === 'success' && pagoSummary && (
          <ReciboPagoSuccessPanel
            facturaRef={
              facturaCtx || {
                id: creditoId,
                numero: 'Crédito',
                total: pagoSummary.saldoAnterior + pagoSummary.monto,
              }
            }
            companyName={currentEmpresa?.nombre || 'Empresa'}
            monto={pagoSummary.monto}
            saldoAnterior={pagoSummary.saldoAnterior}
            saldoDespues={pagoSummary.saldoDespues}
            receipt={receipt}
            onClose={handleDone}
          />
        )}
        {phase === 'form' && (
        <form onSubmit={handleSubmit} className="space-y-3 p-4">
          {loading ? (
            <p className="text-sm text-gray-500">Cargando cuotas…</p>
          ) : cuotas.length === 0 ? (
            <p className="text-sm text-amber-800">No hay cuotas pendientes para este crédito.</p>
          ) : (
            <>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase text-gray-500">Cuota</label>
                <select
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  value={cuotaId}
                  onChange={(e) => setCuotaId(e.target.value)}
                >
                  {cuotas.map((c) => (
                    <option key={c.id} value={c.id} disabled={c.estado === 'pagada'}>
                      #{c.numero_cuota} · vence {c.fecha_vencimiento} · saldo {fmtCop(saldoCuota(c))} · {c.estado}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase text-gray-500">Monto (COP)</label>
                <input
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  inputMode="numeric"
                  value={monto}
                  onChange={(e) => setMonto(e.target.value)}
                />
                {selected ? (
                  <p className="mt-1 text-[11px] text-gray-500">
                    Saldo de la cuota: {fmtCop(saldoSel)}. Se generará un recibo de este abono.
                  </p>
                ) : null}
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase text-gray-500">Método de pago</label>
                <select
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  value={metodo}
                  onChange={(e) => setMetodo(e.target.value)}
                >
                  {methodOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase text-gray-500">Notas (opcional)</label>
                <textarea
                  className="min-h-[72px] w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  value={notas}
                  onChange={(e) => setNotas(e.target.value)}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase text-gray-500">Comprobante (opcional)</label>
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  className="w-full text-sm text-gray-600"
                  onChange={(e) => setComprobanteFile(e.target.files?.[0] ?? null)}
                />
              </div>
              <p className="rounded-lg bg-slate-50 p-2 text-[11px] text-gray-600">
                El sistema actualizará cuota, crédito y factura, y generará el recibo para el cliente.
              </p>
              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  className="flex-1 rounded-lg border border-gray-300 py-2.5 text-sm font-medium text-gray-700"
                  onClick={onClose}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submitting || !cuotaId}
                  className="flex-1 rounded-lg py-2.5 text-sm font-medium text-white disabled:opacity-50"
                  style={{ backgroundColor: '#1A56DB' }}
                >
                  {submitting ? 'Guardando…' : 'Guardar pago'}
                </button>
              </div>
            </>
          )}
        </form>
        )}
      </div>
    </div>
  );
}
